import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Home, 
  Users, 
  Award, 
  MessageCircle,
  Heart,
  Building,
  Truck,
  FileText,
  MapPin,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Info,
  Star,
  Calendar,
  Clock,
  Map,
  Compass,
  Flag,
  Target,
  Key,
  Lock,
  Mail,
  Phone,
  Package,
  ShoppingCart,
  Gift,
  Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import ChatBot from '@/components/ChatBot';
import DailyLoginReward from '@/components/DailyLoginReward';
import { api } from '@/App';

// Icon mapping
const iconMap = {
  BookOpen,
  Home,
  Users,
  MessageCircle,
  Heart,
  Building,
  Truck,
  FileText,
  MapPin,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Info,
  Star,
  Calendar,
  Clock,
  Map,
  Compass,
  Flag,
  Target,
  Key,
  Lock,
  Mail,
  Phone,
  Package,
  ShoppingCart,
  Gift,
  Award
};

const Dashboard = ({ user, progress, logout }) => {
  const [showChat, setShowChat] = useState(false);
  const [dashboardModules, setDashboardModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComingSoonDialog, setShowComingSoonDialog] = useState(false);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    fetchDashboardModules();
  }, []);

  const fetchDashboardModules = async () => {
    try {
      const response = await api.get('/dashboard-modules');
      setDashboardModules(response.data);
    } catch (error) {
      console.error('Error fetching dashboard modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await api.post('/redeem-coupon', { code: couponCode });
      
      if (response.data.discount_type === 'coins') {
        toast.success(`🎉 You received ${response.data.coins_awarded} coins!`, { duration: 5000 });
      } else {
        toast.success(`Coupon applied! ${response.data.description || response.data.code}`);
      }
      
      setShowCouponDialog(false);
      setCouponCode('');
      
      // Refresh the page to update coin count
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || Award;
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div data-testid="dashboard-welcome" className="mb-12 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{color: '#D66238'}}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <div className="flex items-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6" style={{color: '#D66238'}} />
              <span className="text-lg font-semibold" style={{color: '#D66238'}}>
                Level {progress?.level || 1}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold" style={{color: '#6FCFD3'}}>
                {progress?.points || 0} Points
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold" style={{color: '#D66238'}}>
                {progress?.badges?.length || 0} Badges
              </span>
            </div>
          </div>
        </div>

        {/* Daily Login Reward */}
        <div className="mb-8">
          <DailyLoginReward onCoinsEarned={(coins) => console.log(`Earned ${coins} coins!`)} />
        </div>

        {/* Redeem Coupon Button */}
        <div className="mb-8">
          <button
            onClick={() => setShowCouponDialog(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105"
            style={{ backgroundColor: '#6FCFD3', color: 'white' }}
          >
            🎟️ Redeem Coupon Code
          </button>
        </div>

        {/* Main Actions - Dynamic Dashboard Modules */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: '#372F2F' }}>Loading dashboard...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {dashboardModules.map((module, index) => {
              const Icon = getIcon(module.icon_name);
              const isRanchette = module.module_key === 'ranchette';
              // Alternate between turquoise (#6FCFD3) and orange (#D66238)
              const alternatingColor = index % 2 === 0 ? '#6FCFD3' : '#D66238';
              
              const moduleContent = (
                <>
                  <div className="flex items-center justify-between mb-4">
                    {/* Show custom icon image if available, otherwise show pre-made icon */}
                    {module.icon_image_url ? (
                      <div className="w-16 h-16 rounded-full bg-white bg-opacity-30 flex items-center justify-center p-3">
                        <img 
                          src={module.icon_image_url} 
                          alt={module.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <Icon className="w-12 h-12 text-white opacity-90" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {module.title}
                  </h2>
                  <p className="text-white opacity-90">
                    {module.description}
                  </p>
                </>
              );
              
              if (isRanchette) {
                return (
                  <div
                    key={module.id}
                    data-testid={`nav-${module.module_key}`}
                    className="group block p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer"
                    style={{backgroundColor: alternatingColor}}
                    onClick={() => setShowComingSoonDialog(true)}
                  >
                    {moduleContent}
                  </div>
                );
              }
              
              return (
                <Link
                  key={module.id}
                  to={module.route}
                  data-testid={`nav-${module.module_key}`}
                  className="group block p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  style={{backgroundColor: alternatingColor}}
                >
                  {moduleContent}
                </Link>
              );
            })}
          </div>
        )}

        {/* Ask Chatbot Section */}
        <div className="mt-12 text-center">
          <Button
            onClick={() => setShowChat(!showChat)}
            className="text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-2xl transition"
            style={{backgroundColor: '#D66238', color: '#fff'}}
            data-testid="chatbot-toggle"
          >
            <MessageCircle className="mr-2 w-6 h-6" />
            {showChat ? 'Close' : 'Ask Our'} AI Chatbot
          </Button>
        </div>
      </div>

      {showChat && (
        <div className="fixed bottom-4 right-4 z-50">
          <ChatBot />
        </div>
      )}

      {/* Coming Soon Dialog for Ranchette */}
      {showComingSoonDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowComingSoonDialog(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ranch-builder/artifacts/hiz2hika_2BD4FC11-076B-493D-9820-358F0A3251B1.png"
                alt="Coming Soon"
                className="w-24 h-24 mx-auto object-contain"
              />
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{color: '#D66238'}}>
              Coming Soon!
            </h3>
            <p className="text-lg mb-6" style={{color: '#372F2F'}}>
              The Ranchette game is currently under development. Check back soon for an exciting ranch-building experience!
            </p>
            <Button
              onClick={() => setShowComingSoonDialog(false)}
              className="w-full text-lg py-3"
              style={{backgroundColor: '#6FCFD3', color: '#fff'}}
            >
              Got it!
            </Button>
          </div>
        </div>
      )}

      {/* Coupon Redemption Dialog */}
      {showCouponDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCouponDialog(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#D66238'}}>
              🎟️ Redeem Coupon Code
            </h3>
            <p className="text-center mb-6" style={{color: '#372F2F'}}>
              Enter your coupon code to receive rewards!
            </p>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="w-full p-3 border-2 rounded-xl text-center font-mono text-lg uppercase mb-4"
              style={{ borderColor: '#6FCFD3' }}
              onKeyPress={(e) => e.key === 'Enter' && handleRedeemCoupon()}
            />
            <div className="flex gap-3">
              <Button
                onClick={() => setShowCouponDialog(false)}
                variant="outline"
                className="flex-1 py-3"
                style={{ borderColor: '#D66238', color: '#D66238' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRedeemCoupon}
                disabled={couponLoading || !couponCode.trim()}
                className="flex-1 py-3"
                style={{ backgroundColor: '#6FCFD3', color: '#fff' }}
              >
                {couponLoading ? 'Redeeming...' : 'Redeem'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
